import { useTranslation } from 'react-i18next';

/**
 * FT-263: Locale-aware date, time, and number formatting.
 *
 * All formatting uses the Intl API with the active i18n locale so that
 * output is automatically correct for each supported language.
 *
 * Storage remains canonical (ISO-8601 / UTC). Formatting only happens
 * at the presentation layer — never before persisting to the API.
 *
 * Gregorian calendar only in v1.
 */

type DateInput = Date | string | null | undefined;

function toDate(input: DateInput): Date | null {
  if (!input) return null;
  const d = input instanceof Date ? input : new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

const DEFAULT_DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
};

/**
 * Hook that returns a locale-aware date-only formatter function.
 *
 * Default output: "Jan 5, 2024" (short month + day + year).
 *
 * @example
 * const formatDate = useFormatDate();
 * formatDate(person.dateOfBirth)         // "Jan 5, 2024" in en-US
 * formatDate(null, 'Unknown')            // "Unknown"
 */
export function useFormatDate(
  options?: Intl.DateTimeFormatOptions,
): (input: DateInput, fallback?: string) => string {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const opts = options ?? DEFAULT_DATE_OPTIONS;
  return (input: DateInput, fallback = ''): string => {
    const d = toDate(input);
    return d ? new Intl.DateTimeFormat(locale, opts).format(d) : fallback;
  };
}

/**
 * Hook that returns a locale-aware date+time formatter function.
 *
 * Default output: "Jan 5, 2024, 3:45 PM" (medium date + short time).
 *
 * @example
 * const formatDateTime = useFormatDateTime();
 * formatDateTime(invite.expiresAt)  // "Jan 5, 2024, 3:45 PM" in en-US
 */
export function useFormatDateTime(
  options?: Intl.DateTimeFormatOptions,
): (input: DateInput, fallback?: string) => string {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const opts = options ?? DEFAULT_DATETIME_OPTIONS;
  return (input: DateInput, fallback = ''): string => {
    const d = toDate(input);
    return d ? new Intl.DateTimeFormat(locale, opts).format(d) : fallback;
  };
}

/**
 * Hook that returns a locale-aware number formatter function.
 *
 * @example
 * const formatNumber = useFormatNumber();
 * formatNumber(1234567)  // "1,234,567" in en-US, "1.234.567" in de-DE
 */
export function useFormatNumber(
  options?: Intl.NumberFormatOptions,
): (value: number | null | undefined, fallback?: string) => string {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  return (value: number | null | undefined, fallback = ''): string => {
    if (value == null || isNaN(value)) return fallback;
    return new Intl.NumberFormat(locale, options).format(value);
  };
}

/**
 * Non-hook variant for date-only formatting.
 *
 * Use inside callbacks, event handlers, or standalone (non-React) functions
 * where hooks cannot be called. Pass `i18n.language` as the locale.
 */
export function formatDate(
  locale: string,
  input: DateInput,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS,
  fallback = '',
): string {
  const d = toDate(input);
  return d ? new Intl.DateTimeFormat(locale, options).format(d) : fallback;
}

/**
 * Non-hook variant for date+time formatting.
 *
 * Use inside callbacks, event handlers, or standalone (non-React) functions
 * where hooks cannot be called. Pass `i18n.language` as the locale.
 */
export function formatDateTime(
  locale: string,
  input: DateInput,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATETIME_OPTIONS,
  fallback = '',
): string {
  const d = toDate(input);
  return d ? new Intl.DateTimeFormat(locale, options).format(d) : fallback;
}
